import { Add } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import AttachmentBox from '../../components/AttachmentBox/AttachmentBox';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import FormatCurrency from '../../components/FormatNumber/FormatCurrency';
import FormatCurrencyInput from '../../components/FormatNumber/FormatCurrencyInput';
import FormatNumberInput from '../../components/FormatNumber/FormatNumberInput';
import LocationInput from '../../components/LocationInput/LocationInput';
import { dashBorder } from '../../constants';
import { InvoiceStatus } from '../../enums/invoiceStatus';
import { useFilesManagement } from '../../hooks/useFilesManagement';
import { FileType } from '../../models/fileType';
import { Invoice } from '../../models/invoice';
import { Manager } from '../../models/manager';
import { fileService } from '../../services/fileService';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900']
}));

interface InvoiceFormProps {
  title: string;
  handleSubmit: (files: File[], isDraft?: boolean) => void;
  isEditing?: boolean;
  isProjectInvoice?: boolean;
  managerList?: Manager[];
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  projectAttachments?: FileType[];
  setInvoiceFilesToDelete?: React.Dispatch<
    React.SetStateAction<FileType[] | undefined>
  >;
  setInvoiceFiles?: React.Dispatch<
    React.SetStateAction<FileType[] | undefined>
  >;
  invoiceFiles?: FileType[];
}
const InvoiceForm = ({
  title,
  handleSubmit,
  isEditing,
  isProjectInvoice,
  setIsEditing,
  managerList,
  projectAttachments,
  setInvoiceFilesToDelete,
  setInvoiceFiles,
  invoiceFiles
}: InvoiceFormProps): JSX.Element => {
  const emptyValue: string[] = ['$00.00', '$0.00', '$.00'];
  const projectAttachInputRef = useRef<HTMLInputElement | null>(null);
  const [currentTotal, setCurrentTotal] = useState<number>(0);

  const {
    control,
    setValue,
    watch,
    clearErrors,
    formState: { errors }
  } = useFormContext<Invoice>();

  const { files, onAttachFile, onEditFile, onDeleteFile } =
    useFilesManagement();

  const total = watch('total');
  const totalHours = watch('totalHours');
  const hourlyRate = watch('hourlyRate');
  const expenses = watch('expenses');
  const status = watch('status');
  const createdOn = watch('createdOn');

  const projectAttachmentHandler = () => {
    if (projectAttachInputRef.current) {
      projectAttachInputRef.current.click();
    }
  };

  const downloadAttachment = (uuid: string) => {
    fileService.getFileUrl(`${uuid}`).then((response) => {
      window.open(response.data, 'blank');
    });
  };

  const cleanCurrencyValue = (value?: string) => {
    if (!value) {
      return '';
    }
    if (emptyValue.find((v) => v === value)) {
      return '';
    }
    return value.replace('$', '').replaceAll(',', '');
  };

  useEffect(() => {
    if (total) {
      if (expenses) {
        setCurrentTotal(Number(expenses) + Number(total));
      } else {
        setCurrentTotal(Number(total));
      }
      setValue('hourlyRate', null);
      setValue('totalHours', null);
    }
  }, [total]);

  useEffect(() => {
    if (totalHours && hourlyRate) {
      if (expenses) {
        setCurrentTotal(
          Number(expenses) + Number(totalHours) * Number(hourlyRate)
        );
      } else {
        setCurrentTotal(Number(totalHours) * Number(hourlyRate));
      }
      setValue('total', null);
      clearErrors('total');
    }
  }, [totalHours, hourlyRate]);

  useEffect(() => {
    if (expenses && total) {
      setCurrentTotal(Number(expenses) + Number(total));
    } else if (expenses && totalHours && hourlyRate)
      setCurrentTotal(
        Number(expenses) + Number(totalHours) * Number(hourlyRate)
      );
  }, [expenses]);

  const handleChangeCurrentTotal = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrentTotal(Number(cleanCurrencyValue(event.target.value)));
    setValue('hourlyRate', null);
    setValue('totalHours', null);
  };

  const onDeleteTalentFile = (fileToRemove: FileType) => {
    if (setInvoiceFiles && setInvoiceFilesToDelete && invoiceFiles?.length) {
      const filesArray = invoiceFiles?.filter(
        (file: FileType) => file.uuid !== fileToRemove.uuid
      );
      setInvoiceFiles(filesArray);
      setInvoiceFilesToDelete((filesToDelete) =>
        filesToDelete ? [...filesToDelete, fileToRemove] : [fileToRemove]
      );
    }
  };

  return (
    <>
      <DetailsCard
        title={title}
        subtitle={
          createdOn
            ? `Submitted on: ${format(new Date(createdOn), 'MMM do, Y')}`
            : ''
        }
        status={InvoiceStatus[status ?? 'NOT_YET_SUBMITTED']}
      >
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
          <Box
            sx={{
              ...dashBorder,
              display: 'flex',
              gap: 3,
              flexDirection: { xs: 'column', lg: 'row' }
            }}
          >
            <Box sx={{ flex: '1 1 0px' }}>
              <Box display="flex" gap={3}>
                <Stack
                  spacing={1}
                  sx={{ flexShrink: 0, justifyContent: 'space-around' }}
                >
                  <BoldText>Project Title:</BoldText>
                  <BoldText>Company:</BoldText>
                  <BoldText>Manager:</BoldText>
                  <BoldText>Talent:</BoldText>
                  <BoldText>Category:</BoldText>
                  <BoldText>Expense Code:</BoldText>
                  <BoldText>Location:</BoldText>
                  <BoldText>Description:</BoldText>
                </Stack>
                <Stack spacing={1} sx={{ flex: '1 0 160px' }}>
                  <Controller
                    name="projectTitle"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        helperText={errors.projectTitle?.message}
                        disabled={isProjectInvoice}
                        sx={{ background: 'white' }}
                        error={!!errors.projectTitle}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="companyName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        helperText={errors.companyName?.message}
                        disabled
                        sx={{ background: 'white' }}
                        error={!!errors.companyName}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="manager"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        onChange={(event, item) => {
                          onChange(item);
                        }}
                        value={value}
                        options={managerList ?? []}
                        getOptionLabel={(option) =>
                          `${option.firstName ?? ''} ${option.lastName ?? ''}`
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        disabled={isProjectInvoice}
                        filterSelectedOptions
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            error={!!errors.manager}
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name={'talentFullName'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        disabled
                        sx={{ background: 'white' }}
                        error={!!errors.talentFullName}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        disabled={isProjectInvoice}
                        sx={{ background: 'white' }}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="expenseCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        disabled={isProjectInvoice}
                        sx={{ background: 'white' }}
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="address"
                    control={control}
                    render={({ field }) => (
                      <LocationInput
                        sx={{ background: 'white' }}
                        disabled={isProjectInvoice}
                        onChangeLocation={(address) =>
                          setValue('address', address)
                        }
                        {...field}
                      />
                    )}
                  />
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        variant="outlined"
                        size="small"
                        disabled={isProjectInvoice}
                        sx={{ background: 'white' }}
                        {...field}
                      />
                    )}
                  />
                </Stack>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0px',
                alignItems: { xs: 'start', lg: 'end' }
              }}
            >
              <Box mb={3}>
                <Typography mb={1}>Project Attachments:</Typography>
                {projectAttachments?.map((file) => (
                  <AttachmentBox
                    key={file.id}
                    onClickFile={() => downloadAttachment(file.uuid)}
                    fileName={file.fileName}
                  />
                ))}
                {!projectAttachments?.length && (
                  <AttachmentBox fileName="No Attachments" />
                )}
              </Box>
              <Box>
                <Typography mb={1}>Talent Attachments:</Typography>
                {invoiceFiles?.map((file) => (
                  <AttachmentBox
                    key={file.id}
                    onClickFile={() => downloadAttachment(file.uuid)}
                    onDeleteFile={() => onDeleteTalentFile(file)}
                    fileName={file.fileName}
                  />
                ))}
                {files?.map((file) => (
                  <AttachmentBox
                    key={file.name}
                    onDeleteFile={() => onDeleteFile(file)}
                    onClickFile={() => {
                      const href = URL.createObjectURL(file);
                      window.open(href, 'blank');
                    }}
                    onEditFile={(fileName) => onEditFile(file, fileName)}
                    fileName={file.name}
                  />
                ))}
                {!files?.length && !invoiceFiles?.length && (
                  <AttachmentBox fileName="No Attachments" />
                )}
                <Button
                  size="medium"
                  variant="lightBlue"
                  fullWidth
                  onClick={projectAttachmentHandler}
                  startIcon={<Add />}
                >
                  Add attachment
                </Button>
                <Box sx={{ display: 'none' }}>
                  <input
                    type="file"
                    multiple
                    title="attachment"
                    ref={projectAttachInputRef}
                    onChange={(event) => onAttachFile(event.target.files)}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              gap: 2,
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' }
            }}
          >
            <Box
              sx={{
                ...dashBorder,
                flex: '1 1 0px'
              }}
            >
              <BoldText>Talent Notes:</BoldText>
              <Controller
                name="talentNotes"
                control={control}
                render={({ field }) => (
                  <TextField multiline rows={8} {...field} />
                )}
              />
            </Box>
            <Box
              sx={{
                ...dashBorder,
                flex: '1 1 0px',
                bgcolor: '#F6F7FB'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { sx: 'start', lg: 'end' },
                  gap: 2
                }}
              >
                <Box
                  sx={{
                    textAlign: { sx: 'start', lg: 'end' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-around'
                  }}
                >
                  <Typography>Project Fee Total:</Typography>
                  <Typography>Total Hours Worked:</Typography>
                  <Typography>Pay/Hour:</Typography>
                  <Typography>Expenses:</Typography>
                </Box>
                <Box
                  textAlign="right"
                  gap={1}
                  display="flex"
                  flexDirection="column"
                >
                  <Controller
                    name="total"
                    control={control}
                    render={({
                      field: { name, onBlur, onChange, ref, value }
                    }) => (
                      <TextField
                        size="small"
                        sx={{ background: 'white' }}
                        onInput={handleChangeCurrentTotal}
                        id="formatted-expenses-input"
                        InputProps={{
                          // eslint-disable-next-line
                          inputComponent: FormatCurrencyInput as any
                        }}
                        variant="outlined"
                        error={!!errors.total}
                        value={value ?? ''}
                        name={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        ref={ref}
                      />
                    )}
                  />
                  <Controller
                    name="totalHours"
                    control={control}
                    render={({
                      field: { name, onBlur, onChange, ref, value }
                    }) => (
                      <TextField
                        size="small"
                        sx={{ background: 'white' }}
                        id="formatted-expenses-input"
                        InputProps={{
                          // eslint-disable-next-line
                          inputComponent: FormatNumberInput as any
                        }}
                        variant="outlined"
                        error={!!errors.totalHours}
                        value={value ?? ''}
                        name={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        ref={ref}
                      />
                    )}
                  />
                  <Controller
                    name="hourlyRate"
                    control={control}
                    render={({
                      field: { name, onBlur, onChange, ref, value }
                    }) => (
                      <TextField
                        size="small"
                        sx={{ background: 'white' }}
                        id="formatted-expenses-input"
                        InputProps={{
                          // eslint-disable-next-line
                          inputComponent: FormatCurrencyInput as any
                        }}
                        variant="outlined"
                        error={!!errors.hourlyRate}
                        value={value ?? ''}
                        name={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        ref={ref}
                      />
                    )}
                  />
                  <Controller
                    name="expenses"
                    control={control}
                    render={({
                      field: { name, onBlur, onChange, ref, value }
                    }) => (
                      <TextField
                        size="small"
                        sx={{ background: 'white' }}
                        id="formatted-expenses-input"
                        InputProps={{
                          // eslint-disable-next-line
                          inputComponent: FormatCurrencyInput as any
                        }}
                        variant="outlined"
                        error={!!errors.expenses}
                        value={value ?? ''}
                        name={name}
                        onBlur={onBlur}
                        onChange={onChange}
                        ref={ref}
                      />
                    )}
                  />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { sx: 'start', lg: 'end' },
                  gap: 2,
                  alignItems: 'center'
                }}
              >
                <Typography>Invoice Total:</Typography>
                <Typography variant="h5" color="grey.900" fontWeight="bold">
                  <FormatCurrency value={currentTotal} />
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            spacing={2}
            mt={1}
          >
            {status !== InvoiceStatus.SUBMITTED &&
              status !== InvoiceStatus.REJECTED && (
                <Button
                  sx={{ width: '180px' }}
                  variant="outlined"
                  onClick={() => handleSubmit(files, true)}
                >
                  Save as Draft
                </Button>
              )}
            {(status === InvoiceStatus.SUBMITTED ||
              status === InvoiceStatus.REJECTED) &&
              isEditing &&
              setIsEditing && (
                <Button
                  sx={{ width: '180px' }}
                  variant="outlined"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              )}
            <Button sx={{ width: '150px' }} onClick={() => handleSubmit(files)}>
              Submit
            </Button>
          </Stack>
        </Box>
      </DetailsCard>
    </>
  );
};

export default InvoiceForm;
